import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { ICON } from '../icons/iconIds';
import IconText from './icons/IconText';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Spinner from './ui/Spinner';
import { plannedHoleLatLng } from '../utils/plannedDrillingCoords';
import {
  formatPlannedScheduleLabel,
  estimatedCompleteIso,
} from '../utils/plannedDrillingSchedule';
import { formatMapHoleLabel } from '../utils/mapHoleLabel';
import {
  buildPadDonutSvg,
  buildTravelBarSvg,
  holeRingColor,
  plannedScheduleWindow,
  travelDayIsosBetween,
} from '../utils/plannedDrillingCalendar';
import './PlannedDrillingMap.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const PAD_CORE_R = 22;
const PAD_RING_W = 5;
const PAD_RING_GAP = 2;
const PAD_MARKER_MIN = 72;
/** Active pad: room for donut + red halo. */
const PAD_MARKER_ACTIVE_MIN = 108;

const PLANNED_DRILL_PALETTE = [
  '#3b82f6',
  '#f97316',
  '#22c55e',
  '#ec4899',
  '#8b5cf6',
  '#14b8a6',
  '#eab308',
  '#ef4444',
];

/** @typedef {{ type: 'in_progress', row: Record<string, unknown> } | { type: 'planned', row: Record<string, unknown> } | null} PlannedMapSelection */

function rigGroupKey(row) {
  const rig = row.drill_rig != null && String(row.drill_rig).trim() !== '' ? String(row.drill_rig).trim() : '';
  const rn = row.rig_number != null && row.rig_number !== '' ? String(row.rig_number) : '__';
  return `${rig}|||${rn}`;
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function normalizePad(raw) {
  if (raw == null || String(raw).trim() === '') return null;
  return String(raw).trim();
}

/** Geometric shorthand for clearing / disturbance hints on pad prep. */
function padShapeCategory(row) {
  const s = row.drill_pad_cleared != null ? String(row.drill_pad_cleared).toLowerCase() : '';
  const t = row.tracking_pad != null ? String(row.tracking_pad).toLowerCase() : '';
  const u = `${s} ${t}`;
  if (/clear|prep/.test(u)) return 'triangle';
  if (/disturb|exist|prior|cleared\s*$/i.test(s)) return 'diamond';
  if (/wet|snow|season|staging/.test(u)) return 'square';
  return 'circle';
}

/** Small numeric / label inside dot (derived from rig). */
function drillNumberPrefix(row) {
  const rn = row.rig_number;
  if (rn != null && String(rn).trim() !== '') {
    return String(rn).trim().replace(/^#/, '').slice(0, 4);
  }
  const name = row.drill_rig != null ? String(row.drill_rig) : '';
  const m = name.match(/(\d{1,3})\s*$/);
  if (m) return m[1];
  const letters = name.replace(/\s+/g, '');
  return letters.slice(0, 3).toUpperCase() || '?';
}

function shortHoleCaptionFromId(holeId) {
  return formatMapHoleLabel(holeId);
}

function shortHoleCaption(row) {
  const hid = row.hole_id;
  if (hid != null && String(hid).trim() !== '') return shortHoleCaptionFromId(String(hid));
  const p = row.prop_hole_id;
  if (p != null && String(p).trim() !== '') {
    const s = String(p).trim();
    return s.length > 14 ? `…${s.slice(-11)}` : s;
  }
  if (row.id != null) return `#${row.id}`;
  return '—';
}

function drillAssignmentLongLabel(row) {
  const name =
    row.drill_rig != null && String(row.drill_rig).trim() !== '' ? String(row.drill_rig).trim() : '';
  const rnRaw = row.rig_number;
  const numStr =
    rnRaw != null && rnRaw !== '' && String(rnRaw).trim() !== '' ? String(rnRaw).trim() : '';

  if (name && numStr) {
    const compact = name.replace(/\s+/g, '').toLowerCase();
    const n = numStr.replace(/^#/, '');
    if (compact === `drill${n}` || compact.endsWith(n) || name.includes(numStr)) return name;
    return `${name} (#${n})`;
  }
  if (name) return name;
  if (numStr) return `Drill ${numStr.replace(/^#/, '')}`;
  return 'Drill —';
}

function isCancelled(row) {
  const s = row.status != null ? String(row.status).toLowerCase() : '';
  return s.includes('cancel');
}

function rowStableKey(row) {
  return row.id != null ? `id:${row.id}` : `prop:${row.prop_hole_id ?? '?'}`;
}

function todayIsoUtc() {
  return new Date().toISOString().slice(0, 10);
}

/** Keep text readable when the segment bearing would flip labels upside-down. */
function labelRotationAlongLine(brng) {
  return brng > 90 && brng < 270 ? brng + 180 : brng;
}

/** Bearing from A → B (degrees clockwise from north), for CSS rotation. */
function bearingDeg(lat1, lng1, lat2, lng2) {
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δλ = ((lng2 - lng1) * Math.PI) / 180;
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  const θ = (Math.atan2(y, x) * 180) / Math.PI;
  return (θ + 360) % 360;
}

function midpointLatLng(latlngA, latlngB) {
  return [(latlngA[0] + latlngB[0]) / 2, (latlngA[1] + latlngB[1]) / 2];
}

function llNearEqual(a, b, epsLat = 1.5e-5, epsLng = 1.5e-5) {
  return Math.abs(a[0] - b[0]) < epsLat && Math.abs(a[1] - b[1]) < epsLng;
}

function buildDrillColorByKeyMap(plannedRows, inProgressHoles, rigColorMap) {
  const keyToSample = new Map();
  for (const r of plannedRows || []) {
    const k = rigGroupKey(r);
    if (!keyToSample.has(k)) keyToSample.set(k, r);
  }
  for (const h of inProgressHoles || []) {
    const k = rigGroupKey(h);
    if (!keyToSample.has(k)) keyToSample.set(k, h);
  }

  const keys = [...keyToSample.keys()].sort((a, b) => a.localeCompare(b));
  const colorByRig = new Map();
  const out = new Map();
  keys.forEach((key, idx) => {
    const row = keyToSample.get(key);
    const dr = row?.drill_rig != null && String(row.drill_rig).trim() !== '' ? String(row.drill_rig).trim() : '';
    if (dr && !colorByRig.has(dr)) {
      let c =
        rigColorMap && typeof rigColorMap === 'object' ? rigColorMap[dr] : null;
      if (!c || String(c).trim() === '') {
        c = PLANNED_DRILL_PALETTE[colorByRig.size % PLANNED_DRILL_PALETTE.length];
      }
      colorByRig.set(dr, c);
    }
    const c = dr
      ? colorByRig.get(dr)
      : PLANNED_DRILL_PALETTE[idx % PLANNED_DRILL_PALETTE.length];
    out.set(key, c);
  });
  return { colorByKey: out, sampleRowByKey: keyToSample };
}

function hashHue(str) {
  let h = 0;
  const t = String(str);
  for (let i = 0; i < t.length; i += 1) {
    h = t.charCodeAt(i) + ((h << 5) - h);
  }
  return Math.abs(h) % 360;
}

function fallbackLineColor(rigKey) {
  const hue = hashHue(rigKey);
  return `hsl(${hue}, 72%, 52%)`;
}

/** @param {Record<string, unknown>[]} plannedRows */
function padCentroidsFromPlanned(plannedRows) {
  const acc = new Map();
  for (const row of plannedRows) {
    const pad = normalizePad(row.pad_id);
    if (!pad) continue;
    const ll = plannedHoleLatLng(row);
    if (!ll) continue;
    if (!acc.has(pad)) acc.set(pad, { sumLat: 0, sumLon: 0, n: 0 });
    const a = acc.get(pad);
    a.sumLat += ll[0];
    a.sumLon += ll[1];
    a.n += 1;
  }
  const out = new Map();
  acc.forEach((v, pad) => {
    out.set(pad, [v.sumLat / v.n, v.sumLon / v.n]);
  });
  return out;
}

function sortPlannedForRig(rows) {
  return [...rows].sort((a, b) => {
    const ia = estimatedCompleteIso(a);
    const ib = estimatedCompleteIso(b);
    const ta = ia ? Date.parse(`${ia}T12:00:00Z`) : Number.POSITIVE_INFINITY;
    const tb = ib ? Date.parse(`${ib}T12:00:00Z`) : Number.POSITIVE_INFINITY;
    if (ta !== tb) return ta - tb;
    const pa = Number(a.priority);
    const pb = Number(b.priority);
    const fa = Number.isFinite(pa);
    const fb = Number.isFinite(pb);
    if (fa && fb && pa !== pb) return pa - pb;
    if (fa !== fb) return fa ? -1 : 1;
    return String(a.prop_hole_id || a.id || '').localeCompare(String(b.prop_hole_id || b.id || ''));
  });
}

/** Map planned row stable key → sequence label `{prefix}-{n}` */
function computePlannedSeqLabels(plannedRows) {
  const byRig = new Map();
  for (const row of plannedRows || []) {
    if (isCancelled(row)) continue;
    if (!estimatedCompleteIso(row)) continue;
    const k = rigGroupKey(row);
    if (!byRig.has(k)) byRig.set(k, []);
    byRig.get(k).push(row);
  }
  const out = new Map();
  byRig.forEach((list) => {
    const sorted = sortPlannedForRig(list);
    const prefix = drillNumberPrefix(sorted[0] ?? list[0]);
    sorted.forEach((row, idx) => {
      out.set(rowStableKey(row), `${prefix}-${idx + 1}`);
    });
  });
  return out;
}

function earliestStartMsOnPad(rows) {
  let min = Number.POSITIVE_INFINITY;
  (rows || []).forEach((row) => {
    const w = plannedScheduleWindow(row);
    if (w) min = Math.min(min, Date.parse(`${w.startIso}T12:00:00Z`));
  });
  return min;
}

function lastEndIsoOnSite(site) {
  if (!site.holeRings.length) return site.isActive ? todayIsoUtc() : null;
  return site.holeRings[site.holeRings.length - 1].endIso;
}

function firstStartIsoOnSite(site) {
  if (!site.holeRings.length) return null;
  return site.holeRings[0].startIso;
}

/**
 * One map marker per pad: merged IP + planned holes, donut day rings per hole.
 * @returns {{ sites: object[], travels: object[] }}
 */
function buildRigPadModel(rigKey, plannedRowsAll, padCentroids, ipHoles, seqLabelByRow, rigColor) {
  const rigsPlanned = sortPlannedForRig(
    (plannedRowsAll || []).filter(
      (r) => rigGroupKey(r) === rigKey && !isCancelled(r) && estimatedCompleteIso(r)
    )
  );

  const ips = (ipHoles || [])
    .filter((h) => rigGroupKey(h) === rigKey)
    .sort((a, b) => String(a.hole_id || '').localeCompare(String(b.hole_id || '')));
  const activeIp = ips[0] || null;
  const activePad = activeIp ? normalizePad(activeIp.pad_id) : null;

  /** @type {Map<string, Record<string, unknown>[]>} */
  const byPad = new Map();
  rigsPlanned.forEach((row) => {
    const p = normalizePad(row.pad_id);
    if (!p) return;
    if (!byPad.has(p)) byPad.set(p, []);
    byPad.get(p).push(row);
  });

  const padIds = new Set(byPad.keys());
  if (activePad) padIds.add(activePad);

  const padOrder = [...padIds].sort((pa, pb) => {
    if (pa === activePad) return -1;
    if (pb === activePad) return 1;
    return earliestStartMsOnPad(byPad.get(pa)) - earliestStartMsOnPad(byPad.get(pb));
  });

  /** @type {object[]} */
  const sites = [];
  padOrder.forEach((padId) => {
    const rows = byPad.get(padId) || [];
    const isActive = activePad === padId;
    let latlng = null;
    if (isActive && activeIp) {
      const lat = Number(activeIp.latitude);
      const lng = Number(activeIp.longitude);
      if (Number.isFinite(lat) && Number.isFinite(lng)) latlng = [lat, lng];
    }
    if (!latlng) latlng = padCentroids.get(padId) || plannedHoleLatLng(rows[0]) || null;
    if (!latlng) return;

    const holeRings = rows
      .map((row, idx) => {
        const win = plannedScheduleWindow(row);
        if (!win) return null;
        return {
          row,
          ...win,
          color: holeRingColor(rigColor, idx),
          holeIndex: idx,
          seqLabel: seqLabelByRow.get(rowStableKey(row)) || `${drillNumberPrefix(row)}-${idx + 1}`,
        };
      })
      .filter(Boolean);

    const refRow = isActive && activeIp ? activeIp : rows[0] || activeIp;
    const prefix = drillNumberPrefix(refRow || { drill_rig: rigKey.split('|||')[0] });
    const centerLabel = isActive ? `${prefix}-Active` : prefix;
    const upcomingCount = isActive ? rows.length : Math.max(0, rows.length - 1);
    const totalAtPad = rows.length + (isActive ? 1 : 0);
    const shapeCat = isActive ? 'circle' : padShapeCategory(rows[0] || {});

    sites.push({
      rigKey,
      padId,
      latlng,
      isActive,
      holeRings,
      centerLabel,
      upcomingCount,
      totalAtPad,
      rows,
      activeIp,
      primaryRow: isActive ? activeIp : rows[0] || null,
      primaryType: isActive ? 'in_progress' : 'planned',
      shapeCat,
      rigColor,
    });
  });

  /** @type {object[]} */
  const travels = [];
  for (let i = 0; i < sites.length - 1; i += 1) {
    const A = sites[i];
    const B = sites[i + 1];
    if (llNearEqual(A.latlng, B.latlng)) continue;
    const lastEnd = lastEndIsoOnSite(A);
    const nextStart = firstStartIsoOnSite(B);
    const dayIsos = travelDayIsosBetween(lastEnd, nextStart);
    if (!dayIsos.length) continue;
    travels.push({
      from: A.latlng,
      to: B.latlng,
      dayIsos,
      rigColor,
      fromPad: A.padId,
      toPad: B.padId,
    });
  }

  return { sites, travels };
}

function padSiteIsSelected(site, selectedFeature) {
  if (!selectedFeature) return false;
  if (selectedFeature.type === 'in_progress' && site.activeIp) {
    return String(selectedFeature.row?.hole_id) === String(site.activeIp.hole_id);
  }
  if (selectedFeature.type === 'planned' && selectedFeature.row?.id != null) {
    return site.rows.some((r) => String(r.id) === String(selectedFeature.row.id));
  }
  return false;
}

function createPadSiteMarkerHtml(site, isSelected) {
  const { isActive, centerLabel, upcomingCount, shapeCat, rigColor, holeRings } = site;
  const minEnv = isActive ? PAD_MARKER_ACTIVE_MIN : PAD_MARKER_MIN;
  const corePx = isActive ? 36 : PAD_CORE_R * 2;
  const border = isSelected ? '3px solid #f9be0b' : isActive ? '3px solid #fff' : '2px solid #fff';
  const shapeClass = `planned-drill-shape planned-drill-shape--${shapeCat}${isActive ? ' planned-drill-shape--ip' : ''}`;

  const probeCx = 56;
  const { slices, outerR } = buildPadDonutSvg(
    holeRings.map((h) => ({ dayIsos: h.dayIsos, color: h.color, holeIndex: h.holeIndex })),
    { cx: probeCx, cy: probeCx, coreR: PAD_CORE_R, ringW: PAD_RING_W, ringGap: PAD_RING_GAP }
  );
  const env = Math.max(minEnv, Math.ceil(outerR * 2) + 20, minEnv);
  const cx = env / 2;
  const scale = outerR > 0 ? (env / 2 - 10) / outerR : 1;
  const slicePaths = slices
    .map(
      (s) =>
        `<path d="${s.d}" fill="${s.fill}" stroke="rgba(15,23,42,0.55)" stroke-width="0.65" opacity="0.92"/>`
    )
    .join('');

  const badge =
    upcomingCount > 0
      ? `<span class="planned-pad-count-badge" title="${upcomingCount} more hole(s) on pad">+${upcomingCount}</span>`
      : site.totalAtPad > 1 && !isActive
        ? `<span class="planned-pad-count-badge">${site.totalAtPad}</span>`
        : '';

  const ipHalo = isActive
    ? `<div class="planned-ip-red-halo" aria-hidden="true"></div><div class="planned-ip-red-halo planned-ip-red-halo--delay" aria-hidden="true"></div>`
    : '';

  const donutLayer =
    slicePaths.length > 0
      ? `<svg class="planned-pad-donut" width="${env}" height="${env}" viewBox="0 0 ${env} ${env}" style="position:absolute;left:0;top:0;pointer-events:none;">
          <g transform="translate(${cx - probeCx * scale}, ${cx - probeCx * scale}) scale(${scale})">${slicePaths}</g>
        </svg>`
      : '';

  return {
    env,
    html: `
    <div class="planned-pad-marker-envelope planned-drill-hitbox${isActive ? ' planned-drill-hitbox--ip' : ''}"
      style="width:${env}px;height:${env}px;position:relative;transform:translate(-50%,-50%);">
      <div data-marker-scale-wrap class="planned-drill-scale-shell"
        style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;transform:scale(${isSelected ? 1.06 : 1});">
        ${donutLayer}
        ${ipHalo}
        <div class="${shapeClass}" style="position:relative;z-index:3;">
          <div class="planned-drill-shape-fill" style="width:${corePx}px;height:${corePx}px;background:${rigColor};border:${border}">
            <span class="planned-drill-shape-text">${escapeHtml(centerLabel)}</span>
          </div>
          ${badge}
        </div>
      </div>
    </div>`,
  };
}

function createPadSiteDivIcon(site, isSelected) {
  const { env, html } = createPadSiteMarkerHtml(site, isSelected);
  return L.divIcon({
    className: 'planned-drill-marker-leaflet',
    html,
    iconSize: [env, env],
    iconAnchor: [env / 2, env / 2],
  });
}

function buildTravelCalendarLayers(travels, lineOpacity) {
  const layers = [];
  travels.forEach((tr) => {
    const mid = midpointLatLng(tr.from, tr.to);
    const brng = labelRotationAlongLine(bearingDeg(tr.from[0], tr.from[1], tr.to[0], tr.to[1]));
    const bar = buildTravelBarSvg(tr.dayIsos, tr.rigColor);
    if (!bar.width) return;

    const tipDays = escapeHtml(tr.dayIsos.join(', '));
    const tipPads = escapeHtml(`${tr.fromPad || '?'} → ${tr.toPad || '?'}`);

    const html =
      `<div class="planned-travel-cal-wrap" style="--b:${brng.toFixed(2)}deg;opacity:${lineOpacity};">` +
      `<div class="planned-travel-cal-bar">${bar.html}</div></div>`;

    const icon = L.divIcon({
      className: 'planned-travel-cal-leaflet',
      html,
      iconSize: [bar.width + 4, bar.height + 4],
      iconAnchor: [(bar.width + 4) / 2, (bar.height + 4) / 2],
    });
    const m = L.marker(mid, { icon, interactive: true, zIndexOffset: -100 });
    m.bindTooltip(
      `<div class="planned-drilling-rigline-tt"><div class="planned-drilling-rigline-tt-drill">Move ${tipPads}</div><div class="planned-drilling-rigline-tt-pads">${tipDays}</div></div>`,
      { sticky: true, opacity: 0.95, className: 'planned-drilling-hover-tip' }
    );
    layers.push(m);

    const guide = L.polyline([tr.from, tr.to], {
      color: tr.rigColor,
      weight: 1,
      opacity: lineOpacity * 0.22,
      dashArray: '2 6',
    });
    layers.push(guide);
  });
  return layers;
}

const HOVER_TIP_OPTS = {
  sticky: true,
  opacity: 0.96,
  direction: 'top',
  offset: [0, -10],
  className: 'planned-drilling-hover-tip',
};

function padSiteTooltipHtml(site) {
  const drill = escapeHtml(drillAssignmentLongLabel(site.primaryRow || { drill_rig: site.rigKey.split('|||')[0] }));
  const padLine = `<div class="planned-hover-meta">Pad <strong>${escapeHtml(site.padId || '—')}</strong> · ${site.totalAtPad} hole(s)</div>`;
  const activeLine = site.isActive
    ? `<div class="planned-hover-meta">Drilling now · <strong>${escapeHtml(shortHoleCaption(site.activeIp))}</strong></div>`
    : '';
  const upcomingLine =
    site.upcomingCount > 0
      ? `<div class="planned-hover-meta">${site.upcomingCount} upcoming on this pad</div>`
      : '';

  let ringsHtml = '';
  site.holeRings.forEach((ring) => {
    const hole = escapeHtml(shortHoleCaption(ring.row));
    const seq = escapeHtml(ring.seqLabel || '—');
    const sched = escapeHtml(formatPlannedScheduleLabel(ring.row));
    ringsHtml += `<div class="planned-hover-meta">◉ ${seq} ${hole} · ${sched}</div>`;
  });

  const shapeNote =
    site.isActive
      ? ''
      : `<div class="planned-hover-meta">Pad prep · ${escapeHtml(site.shapeCat)}</div>`;

  return (
    `<div class="planned-hover-block">` +
    `<strong class="planned-hover-drill">${drill}</strong>` +
    padLine +
    activeLine +
    upcomingLine +
    ringsHtml +
    shapeNote +
    `<div class="planned-hover-hint">Donut = drill days · click for details</div>` +
    `</div>`
  );
}

function applyMarkerHoverScale(marker, hovered) {
  const wrap = marker.getElement()?.querySelector('[data-marker-scale-wrap]');
  if (!wrap) return;
  const base = marker._plannedBaseScale ?? 1;
  wrap.style.transform = `scale(${hovered ? base * 1.06 : base})`;
}

export default function PlannedDrillingMap({
  plannedRows,
  inProgressHoles,
  rigColorMap = {},
  loading,
  windowStartISO,
  windowEndISO,
  onSelectFeature,
  selectedFeature,
}) {
  const mapRef = useRef(null);
  const mapInstRef = useRef(null);
  const layersMainRef = useRef(null);
  const onSelectFeatureRef = useRef(onSelectFeature);
  onSelectFeatureRef.current = onSelectFeature;

  const [leafletReady, setLeafletReady] = useState(false);

  const [lineOpacity, setLineOpacity] = useState(0.88);

  const padCentroids = useMemo(() => padCentroidsFromPlanned(plannedRows || []), [plannedRows]);
  const seqLabelByRow = useMemo(() => computePlannedSeqLabels(plannedRows || []), [plannedRows]);
  const lastFitKeyRef = useRef('');

  const { colorByKey, sampleRowByKey } = useMemo(
    () => buildDrillColorByKeyMap(plannedRows, inProgressHoles, rigColorMap),
    [plannedRows, inProgressHoles, rigColorMap]
  );

  const drillLegendEntries = useMemo(() => {
    const seen = new Set();
    const entries = [];
    sampleRowByKey.forEach((row, k) => {
      const rigName =
        row?.drill_rig != null && String(row.drill_rig).trim() !== ''
          ? String(row.drill_rig).trim()
          : drillAssignmentLongLabel(row);
      if (seen.has(rigName)) return;
      seen.add(rigName);
      entries.push({
        key: k,
        color: (rigColorMap && rigColorMap[rigName]) || colorByKey.get(k),
        label: rigName,
      });
    });
    return entries.sort((a, b) => a.label.localeCompare(b.label));
  }, [colorByKey, sampleRowByKey, rigColorMap]);

  /** Unique rigs with planned + IP geometry for timelines. */
  const rigKeysForTimeline = useMemo(() => {
    const ks = new Set();
    for (const r of plannedRows || []) {
      if (!isCancelled(r) && normalizePad(r.pad_id) && estimatedCompleteIso(r)) {
        ks.add(rigGroupKey(r));
      }
    }
    for (const h of inProgressHoles || []) ks.add(rigGroupKey(h));
    return [...ks].sort((a, b) => a.localeCompare(b));
  }, [plannedRows, inProgressHoles]);

  const redraw = useCallback(() => {
    const map = mapInstRef.current;
    if (!map || !layersMainRef.current) return;

    layersMainRef.current.clearLayers();

    const fg = layersMainRef.current;

    /** Travel calendars + pad sites (one marker per pad). */
    rigKeysForTimeline.forEach((rigKey) => {
      const rigColor = colorByKey.get(rigKey) || fallbackLineColor(rigKey);
      const { sites, travels } = buildRigPadModel(
        rigKey,
        plannedRows || [],
        padCentroids,
        inProgressHoles || [],
        seqLabelByRow,
        rigColor
      );

      buildTravelCalendarLayers(travels, lineOpacity).forEach((ly) => ly.addTo(fg));

      sites.forEach((site) => {
        const isSel = padSiteIsSelected(site, selectedFeature);
        const icon = createPadSiteDivIcon(site, isSel);
        const marker = L.marker(site.latlng, {
          icon,
          zIndexOffset: site.isActive ? (isSel ? 2400 : 1800) : isSel ? 1400 : 600,
        });
        marker._plannedBaseScale = isSel ? 1.05 : 1;

        marker.bindTooltip(padSiteTooltipHtml(site), HOVER_TIP_OPTS);

        marker.on('mouseover', () => {
          applyMarkerHoverScale(marker, true);
          marker.setZIndexOffset(site.isActive ? (isSel ? 4400 : 3800) : isSel ? 3400 : 2800);
        });
        marker.on('mouseout', () => {
          applyMarkerHoverScale(marker, false);
          marker.setZIndexOffset(site.isActive ? (isSel ? 2400 : 1800) : isSel ? 1400 : 600);
        });

        marker.on('click', (e) => {
          L.DomEvent.stopPropagation(e);
          if (site.primaryRow) {
            onSelectFeature?.({ type: site.primaryType, row: site.primaryRow });
          }
        });
        marker.addTo(fg);
      });
    });

    const bounds = fg.getBounds();
    if (bounds.isValid()) {
      const fitKey = `${windowStartISO}|${windowEndISO}|${(plannedRows || []).length}|${(inProgressHoles || []).length}|${rigKeysForTimeline.join(',')}`;
      if (lastFitKeyRef.current !== fitKey) {
        map.fitBounds(bounds.pad(0.08));
        lastFitKeyRef.current = fitKey;
      }
    }
  }, [
    plannedRows,
    inProgressHoles,
    padCentroids,
    seqLabelByRow,
    lineOpacity,
    windowEndISO,
    windowStartISO,
    onSelectFeature,
    selectedFeature,
    colorByKey,
    rigKeysForTimeline,
  ]);

  useEffect(() => {
    if (!leafletReady) return undefined;
    redraw();
    return undefined;
  }, [leafletReady, redraw]);

  useEffect(() => {
    if (!mapRef.current || mapInstRef.current) return undefined;

    const map = L.map(mapRef.current, {
      center: [62.5, -137.2],
      zoom: 10,
      zoomControl: true,
      preferCanvas: false,
    });

    const satelliteMap = L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      {
        attribution: '© Esri',
        maxZoom: 18,
      }
    );
    satelliteMap.addTo(map);

    const baseLayers = {
      'Satellite': satelliteMap,
      'Street': L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap',
      }),
    };
    L.control.layers(baseLayers, {}, { position: 'topleft' }).addTo(map);
    L.control.scale({ position: 'bottomleft' }).addTo(map);

    const layersMain = L.featureGroup([]);
    layersMain.addTo(map);
    layersMainRef.current = layersMain;
    mapInstRef.current = map;

    map.on('click', () => {
      onSelectFeatureRef.current?.(null);
    });

    const ro = new ResizeObserver(() => {
      map.invalidateSize({ animate: false });
    });
    ro.observe(mapRef.current);

    setLeafletReady(true);

    return () => {
      setLeafletReady(false);
      ro.disconnect();
      map.remove();
      mapInstRef.current = null;
      layersMainRef.current = null;
    };
  }, []);

  return (
    <div className="planned-drilling-map-root">
      <div className="planned-drilling-map-toolbar">
        <label>
          Calendar opacity
          <input
            type="range"
            min={15}
            max={100}
            value={Math.round(lineOpacity * 100)}
            onChange={(e) => setLineOpacity(Number(e.target.value) / 100)}
          />
        </label>
        <span style={{ color: '#6b7280' }}>
          Pad donuts = drill days · bars = move days · {windowStartISO} … {windowEndISO}
        </span>
      </div>

      <div className="planned-drilling-map-container">
        {loading ? (
          <div className="planned-drilling-map-loading">
            <Spinner />
            <span>Loading planned drilling…</span>
          </div>
        ) : null}

        <div className="planned-map-legend">
          <IconText icon={ICON.uiTools} as="h4">Planned drilling calendar</IconText>
          <div className="planned-map-legend-list">
            {drillLegendEntries.map((e) => (
              <div key={e.key} className="planned-map-legend-row">
                <span className="planned-map-legend-swatch" style={{ background: e.color }} />
                <span>{e.label}</span>
              </div>
            ))}
            <div className="planned-map-legend-row">
              <span className="planned-map-legend-swatch planned-map-legend-swatch--ip" />
              <span>Active pad (red ring)</span>
            </div>
          </div>
          <div className="planned-map-legend-shapes">
            One symbol per pad · +N = more holes · donut = drill days per hole
          </div>
          <div className="planned-map-legend-hint">Move bars = travel days between pads (◇ □ △ only on unused pads)</div>
        </div>

        <div ref={mapRef} className="planned-drilling-map-inner" />
      </div>
    </div>
  );
}
